import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { HttpRequest } from '../HttpRequest';
import { useDispatch } from 'react-redux';
import { setBanners, setButtons, setColors, setDisclaimer, setLayout, setLogo, setPreOrderBadgeType, setSocialMedia, setTemplate, setTopBar, setTypography, setTrustBadges, setTestimonials, setFanCommunity, setWorkingHours } from '../../features/template';
import useGetTemplates from '../common/templates';
import { setBusinessAddress, setBusinessEmail, setBusinessName, setBusinessPhone, setEvoucherEnabled, setFeaturedTeamsEnabled } from '../../features/configsSlice';
import Header from '../../v2/components/Header';
import Footer from '../../v2/components/Footer';
import businessService from '../../v2/services/businessService';
import useTopBarVisible from '../../v2/hooks/useTopBarVisible';
import BillingStatusGate from '../common/BillingStatusGate';

const BusinessRoutes = ({ isNotAuthRoute }) => {
  const [loading, setLoading] = useState(true);
  const [, setTokenGenerated] = useState(false);
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const templates = useGetTemplates();
  const topBarVisible = useTopBarVisible();

  const updateTemplate = (selectedTemplate) => {
    dispatch(setTemplate(selectedTemplate?.template));
    dispatch(setLogo(selectedTemplate?.logo));
    dispatch(setColors(selectedTemplate?.colors));
    dispatch(setTypography(selectedTemplate?.typography));
    dispatch(setButtons(selectedTemplate?.buttons));
    dispatch(setLayout(selectedTemplate?.layout));
    // API historically returned `banner` (singular); newer saves also include `banners`
    dispatch(setBanners(selectedTemplate?.banners || selectedTemplate?.banner));
    dispatch(setPreOrderBadgeType(selectedTemplate?.preOrderBadgeType));
    dispatch(setDisclaimer(selectedTemplate?.disclaimer));
    dispatch(setTopBar(selectedTemplate?.topBar));
    dispatch(setSocialMedia(selectedTemplate?.socialMedia));
    dispatch(setTrustBadges(selectedTemplate?.trustBadges));
    dispatch(setTestimonials(selectedTemplate?.testimonials));
    dispatch(setFanCommunity(selectedTemplate?.fanCommunity));
    dispatch(setWorkingHours(selectedTemplate?.workingHours));
  };

  const getBusinessToken = async () => {
    try {
      const currentDomain = window.location.origin;
      const response = await HttpRequest({
        method: 'GET',
        url: `/api/v1/ecommerce/getzedecommerce-domain-details?subdomain=${currentDomain}`,
      });

      if (response?.status === 200) {
        const businessDetails = response?.response;

        // Use businessService to ensure businessId is properly set
        businessService.setBusinessId(businessDetails?.businessNumber);

        // Update Redux store with business details
        dispatch(setBusinessName(businessDetails?.businessName));
        dispatch(setBusinessEmail(businessDetails?.businessOwnerEmail));
        dispatch(setBusinessPhone(businessDetails?.businessOwnerPhone));
        dispatch(setBusinessAddress(businessDetails?.businessOwnerAddress));
        dispatch(setEvoucherEnabled(businessDetails?.ecommerceVoucherEnabled ?? false));
        dispatch(setFeaturedTeamsEnabled(businessDetails?.featuredTeamsEnabled ?? false));
        // Update template
        let templateData = response?.response?.ecommerceTemplateConfig;
        updateTemplate(templateData || templates['Zedify']);

        setTokenGenerated(true);
      }
    } catch (error) {
      console.error('Error fetching business token:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.has('action') && params.has('template')) {
      const selectedTemplate = params.get('template');
      updateTemplate(templates[selectedTemplate]);
      setLoading(false);
    } else {
      getBusinessToken();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <Box className="storefront" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {loading  ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <BillingStatusGate>
          {isNotAuthRoute && (
            <Header />
          )}
          <Box sx={{
            flexGrow: 1,
            width: '100%',
            marginTop: isNotAuthRoute ? (topBarVisible ? { xs: '110px', md: '130px' } : { xs: '82px', md: '102px' }) : '0',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Outlet />
          </Box>
          {isNotAuthRoute && (
            <Footer />
          )}
        </BillingStatusGate>
      )}
    </Box>
  );
};

export default BusinessRoutes;
